/* eslint-disable indent */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  memo,
} from 'react';
import { useFormContext } from 'react-hook-form';

import {
  applyMoqFloorToMinQuantityTiers,
  resolveStoreCartDesignGroupMoqFloor,
  shouldResetStoreCartDesignGroupToDefaultMinimum,
  STORE_CART_DRAWER_MIN_QUANTITY_TIERS,
  getDistinctBreakpointQuantitiesFromPriceList,
  getStoreCartDrawerMinQuantityTiers,
  getStoreCartPriceFromPriceListOnly,
  isFreshPrintsApparelBrand,
} from '../helpers/storeCartPricing.utils';
import {
  getStoreCartDesignDetailsProofId,
  persistStoreCartDesignGroupMinimums,
} from '../helpers/storeCartNavigation.utils';
import {
  applyStoreCartDrawerDesignGroupTierPrices,
  applyStoreCartQuoteToFormItem,
  fetchStoreCartItemQuote,
  getStoreCartDesignGroupReferencePriceList,
  hasUsableStoreCartPriceList,
} from '../helpers/storeCartQuote.utils';
import {
  persistStoreCartDesignGroupItemUpdates,
  persistStoreCartDesignGroupSelection,
} from '../helpers/storeCartApiSync.utils';
import {
  getStoreCartDesignGroupApiStatus,
  hasStoreCartDesignGroupQuoteFailed,
  isStoreCartDesignGroupMoqPending,
  isStoreCartDesignGroupQuotePending,
  isStoreCartItemBlockedForCheckout,
  shouldShowStoreCartDesignGroupMoqTiers,
  shouldShowStoreCartDesignGroupPrice,
} from '../helpers/storeCartSetup.utils';
import type { IQuoteBreakpoints } from '@/common/interfaces/quoter.interface';
import { useGetQuoteMutation } from '@/api/QuoterQuery';
import { useGetSimilarProductsQuery } from '@/api/SimilarProductQuery';
import StockInfoCard from '@/components/stockComms/stockDetailsCard/StockInfoCard';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setDesignGroupQuoteStatus,
  setStoreCartDesignGroupSelection,
} from '@/store/reducers/storeCart';
import { ViewPort } from '@/common/context/ViewPort/ViewPort';
import useViewport from '@/common/context/ViewPort/useViewPort';
import ImageWrapper from '@/components/ImageWrapper/ImageWrapper';
import { Text } from '@/components/Text/Text';
import Icon from '@/components/Icon/Icon';
import { ToolTip, getBodyTooltipContainer } from '@/components/Tooltip/Tooltip';
import { currencyFormat } from '@/utilities/CommonUtils';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import ColorPill from '@/components/Colorpill/Colorpill';
import {
  STORE_CART_ADD_COLOR_OPTION_ARIA_LABEL,
  STORE_CART_ADD_PRODUCT_OPTION_ARIA_LABEL,
  STORE_CART_DESELECT_COLOR_OPTION_ARIA_LABEL,
  STORE_CART_DESELECT_PRODUCT_OPTION_ARIA_LABEL,
  STORE_CART_OPTION_CANCEL_ICON_SRC,
  STORE_CART_OPTION_RESELECT_ICON_SRC,
  STORE_CART_SELECT_COLOR_OPTION_ARIA_LABEL,
  STORE_CART_SELECT_PRODUCT_OPTION_ARIA_LABEL,
  STORE_CART_ADD_COLOR_OPTION_ERROR_MSG,
  STORE_CART_OPTION_INCLUSION_ERROR_MSG,
  STORE_CART_DESIGN_TOOL_COLOR_NAV_ERROR_MSG,
  STORE_CART_DESIGN_TOOL_PRODUCT_NAV_ERROR_MSG,
  STORE_CART_DESIGN_GROUP_HELP_TOOLTIP,
  STORE_MODAL_TOOLTIP_Z_INDEX,
  STORE_CART_PRICE_LOADING_ARIA_LABEL,
  STORE_CART_MOQ_LOADING_ARIA_LABEL,
  STORE_CART_IMAGE_CAROUSEL_CHEVRON_LEFT_SRC,
  STORE_CART_IMAGE_CAROUSEL_CHEVRON_RIGHT_SRC,
  STORE_CART_IMAGE_CAROUSEL_NAV_PREVIOUS_ARIA_LABEL,
  STORE_CART_IMAGE_CAROUSEL_NAV_NEXT_ARIA_LABEL,
} from '@/modules/Cart/StoreCart/storeCart.constants';
import { DESIGN_TOOL } from '@/constants/Config';
import {
  DESIGN_TOOL_OPTIONS_DISABLED_TOOLTIP,
  MTO_GROUP_ADD_DISABLED_TOOLTIP,
} from '@/constants/Design';
import { colorNameIncludesMto } from '@/utilities/ApparelUtils';
import { checkIfMTO } from '@/utilities/PricingUtils';
import {
  buildDesignGroupDesignToolUrl,
  DESIGN_TOOL_FROM_DESIGN_GROUP_HQ_CART,
} from '@/utilities/DesignToolUrlUtils';
import useRedirectWithUTM from '@/hooks/useRedirectWithUTM';
import { useCartActions } from '@/hooks/useCartActions';
import {
  buildStoreCartAddPayloadForColorOption,
  buildStoreCartOptionSelectionKey,
  clearProductColorSelectionKeys,
  countSelectedColorsForProduct,
  ensureProductHasDefaultColorSelected,
  findFirstSelectedColorForProduct,
  resolveNextColorPreviewAfterDeselect,
  resolveNextProductPreviewAfterDeselect,
  isColorOptionInCart,
  isProductOptionInCart,
  isLightStoreCartColorHex,
  buildCartOnlyProductOptionsFromItems,
  getDesignGroupProductOptionsKey,
  mergeDesignGroupProductOptions,
  normalizeStoreCartProductStyleCode,
  resolveStoreCartDesignGroupMinQuantity,
} from '@/modules/Cart/StoreCart/helpers/storeCartDesignOptions.utils';
import {
  areStoreCartDesignGroupSelectionsEqual,
  buildStoreCartDesignGroupSelection,
  isCartItemIncludedInStoreOptionSelection,
  isEmptyStoreCartDesignGroupSelection,
  serializeStoreCartDesignGroupSelection,
  type IStoreCartDesignGroupSelection,
  wouldLeaveNoColorOptionsSelected,
  wouldLeaveNoProductOptionsSelected,
} from '@/modules/Cart/StoreCart/helpers/storeCartOptionSelection.utils';
import { getStoreCartItemKeys } from '@/modules/Cart/StoreCart/helpers/storeCartRemoval.utils';
import { useStoreCartDesignGroupProductOptions } from '@/hooks/useStoreCartDesignGroupProductOptions';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type {
  IDesignGroupColorOption,
  IDesignGroupProductOption,
} from '@/common/interfaces/design.interface';
import { getStoreDesignMinimumSubtitle } from '@/modules/Orders/CreateOrders/StoreCheckout/constants/storeCheckout.constants';
import './StoreCartDesign.scss';

const STORE_CART_IMAGE_CAROUSEL_TRANSITION_MS_MOBILE = 320;
const STORE_CART_IMAGE_CAROUSEL_TRANSITION_MS_DESKTOP = 380;
const STORE_CART_IMAGE_CAROUSEL_SWIPE_THRESHOLD_RATIO = 0.18;

const getStoreCartCarouselActiveDotIndex = (trackIndex: number, imageCount: number): number => {
  if (imageCount <= 1) {
    return 0;
  }

  if (trackIndex === 0) {
    return imageCount - 1;
  }

  if (trackIndex === imageCount + 1) {
    return 0;
  }

  return trackIndex - 1;
};

// Component for image with arrow navigation (desktop) and touch/swipe (mobile) navigation
const HoverableImage: React.FC<{
  item: ICartItemDetail;
  styleClass: string;
  forceDesktopStyle?: boolean;
}> = memo(({ item, styleClass: itemStyleClass, forceDesktopStyle = false }) => {
  // Get all available images for this item
  const itemImages = useMemo(() => {
    const images: string[] = [];
    if (item.primaryImage) images.push(item.primaryImage);
    if (item.frontImage && !images.includes(item.frontImage)) images.push(item.frontImage);
    if (item.backImage && !images.includes(item.backImage)) images.push(item.backImage);
    if (item.designImgPrimary && !images.includes(item.designImgPrimary)) {
      images.push(item.designImgPrimary);
    }
    return images.length > 0 ? images : [item.primaryImage || item.frontImage || ''];
  }, [item]);

  const imageCount = itemImages.length;
  const isInfiniteCarousel = imageCount > 1;

  const carouselSlides = useMemo(() => {
    if (!isInfiniteCarousel) {
      return itemImages;
    }

    return [itemImages[imageCount - 1], ...itemImages, itemImages[0]];
  }, [imageCount, isInfiniteCarousel, itemImages]);

  const [trackIndex, setTrackIndex] = useState(isInfiniteCarousel ? 1 : 0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const touchStartX = useRef(0);
  const dragOffsetRef = useRef(0);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const itemImagesKey = itemImages.join('|');

  const updateDragOffset = useCallback((offset: number) => {
    dragOffsetRef.current = offset;
    setDragOffset(offset);
  }, []);

  // Check if it's desktop view (not mobile) - respect forceDesktopStyle
  const isDesktop =
    forceDesktopStyle ||
    (!itemStyleClass.includes('--mobile') && !itemStyleClass.includes('--tab'));

  const transitionDurationMs = isDesktop
    ? STORE_CART_IMAGE_CAROUSEL_TRANSITION_MS_DESKTOP
    : STORE_CART_IMAGE_CAROUSEL_TRANSITION_MS_MOBILE;

  const activeDotIndex = getStoreCartCarouselActiveDotIndex(trackIndex, imageCount);

  const trackTransform = useMemo(() => {
    const baseOffsetPercent = -trackIndex * 100;
    const containerWidth = mainImageRef.current?.offsetWidth ?? 0;

    if (!isDragging || containerWidth <= 0) {
      return `translateX(${baseOffsetPercent}%)`;
    }

    const dragPercent = (dragOffset / containerWidth) * 100;
    return `translateX(${baseOffsetPercent + dragPercent}%)`;
  }, [dragOffset, isDragging, trackIndex]);

  const trackTransition =
    isDragging || !transitionEnabled
      ? 'none'
      : `transform ${transitionDurationMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;

  const jumpToTrackIndexWithoutTransition = useCallback((nextTrackIndex: number) => {
    setTransitionEnabled(false);
    setTrackIndex(nextTrackIndex);
  }, []);

  const handleTrackTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== 'transform' || event.target !== event.currentTarget) {
        return;
      }

      if (!isInfiniteCarousel) {
        return;
      }

      if (trackIndex === 0) {
        jumpToTrackIndexWithoutTransition(imageCount);
        return;
      }

      if (trackIndex === imageCount + 1) {
        jumpToTrackIndexWithoutTransition(1);
      }
    },
    [imageCount, isInfiniteCarousel, jumpToTrackIndexWithoutTransition, trackIndex]
  );

  useLayoutEffect(() => {
    if (!transitionEnabled) {
      requestAnimationFrame(() => {
        setTransitionEnabled(true);
      });
    }
  }, [transitionEnabled, trackIndex]);

  useEffect(() => {
    setTrackIndex(isInfiniteCarousel ? 1 : 0);
    updateDragOffset(0);
    setIsDragging(false);
  }, [isInfiniteCarousel, itemImagesKey, updateDragOffset]);

  const handleDotClick = (index: number) => {
    if (!isInfiniteCarousel) {
      return;
    }

    setTrackIndex(index + 1);
  };

  const handleNextImage = useCallback(() => {
    if (isInfiniteCarousel) {
      setTrackIndex((prevIndex) => prevIndex + 1);
    }
  }, [isInfiniteCarousel]);

  const handlePrevImage = useCallback(() => {
    if (isInfiniteCarousel) {
      setTrackIndex((prevIndex) => prevIndex - 1);
    }
  }, [isInfiniteCarousel]);

  const finishDrag = useCallback(
    (offset: number) => {
      const containerWidth = mainImageRef.current?.offsetWidth ?? 0;
      const swipeThreshold = containerWidth * STORE_CART_IMAGE_CAROUSEL_SWIPE_THRESHOLD_RATIO;

      if (Math.abs(offset) > swipeThreshold) {
        if (offset < 0) {
          handleNextImage();
        } else {
          handlePrevImage();
        }
      }

      updateDragOffset(0);
      setIsDragging(false);
    },
    [handleNextImage, handlePrevImage, updateDragOffset]
  );

  const handlePrevNavClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handlePrevImage();
  };

  const handleNextNavClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleNextImage();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isInfiniteCarousel) {
      return;
    }

    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !isInfiniteCarousel) {
      return;
    }

    updateDragOffset(e.touches[0].clientX - touchStartX.current);
  };

  const handleTouchEnd = () => {
    if (!isDragging) {
      return;
    }

    finishDrag(dragOffsetRef.current);
    touchStartX.current = 0;
  };

  return (
    <div className={`${itemStyleClass}__image-wrapper`}>
      <div
        ref={mainImageRef}
        className={`${itemStyleClass}__main-image`}
        onTouchStart={isDesktop ? undefined : handleTouchStart}
        onTouchMove={isDesktop ? undefined : handleTouchMove}
        onTouchEnd={isDesktop ? undefined : handleTouchEnd}
      >
        <div
          className={`${itemStyleClass}__main-image__track`}
          style={{
            transform: trackTransform,
            transition: trackTransition,
          }}
          onTransitionEnd={handleTrackTransitionEnd}
        >
          {carouselSlides.map((imageUrl, index) => (
            <div
              key={`${imageUrl}-${index}`}
              className={`${itemStyleClass}__main-image__slide`}
              aria-hidden={trackIndex !== index}
            >
              {imageUrl ? (
                <ImageWrapper
                  size="medium"
                  imgUrl={imageUrl}
                  fallbackSrc="/src/assets/svg/image_error.svg"
                  className={`${itemStyleClass}__main-image__img`}
                />
              ) : (
                <div className={`${itemStyleClass}__main-image__placeholder`}>
                  <Icon source="/src/assets/svg/image_error.svg" width="100px" height="100px" />
                </div>
              )}
            </div>
          ))}
        </div>
        {isDesktop && isInfiniteCarousel && (
          <>
            <button
              type="button"
              className={`${itemStyleClass}__carousel-nav-btn ${itemStyleClass}__carousel-nav-btn--prev`}
              aria-label={STORE_CART_IMAGE_CAROUSEL_NAV_PREVIOUS_ARIA_LABEL}
              onClick={handlePrevNavClick}
            >
              <Icon
                source={STORE_CART_IMAGE_CAROUSEL_CHEVRON_LEFT_SRC}
                width="11px"
                height="11px"
                className={`${itemStyleClass}__carousel-nav-icon`}
              />
            </button>
            <button
              type="button"
              className={`${itemStyleClass}__carousel-nav-btn ${itemStyleClass}__carousel-nav-btn--next`}
              aria-label={STORE_CART_IMAGE_CAROUSEL_NAV_NEXT_ARIA_LABEL}
              onClick={handleNextNavClick}
            >
              <Icon
                source={STORE_CART_IMAGE_CAROUSEL_CHEVRON_RIGHT_SRC}
                width="11px"
                height="11px"
                className={`${itemStyleClass}__carousel-nav-icon`}
              />
            </button>
          </>
        )}
        {/* Carousel dots - positioned inside the image div */}
        {isInfiniteCarousel && (
          <div className={`${itemStyleClass}__carousel-dots`}>
            {itemImages.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`${itemStyleClass}__carousel-dot ${
                  activeDotIndex === index ? `${itemStyleClass}__carousel-dot--active` : ''
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

HoverableImage.displayName = 'HoverableImage';

interface StoreCartDesignProps {
  cartDesign: ICartItemDetail;
  formIndex: number;
  onCartItemRemove: (formIndex: number) => void | Promise<void>;
  onCartItemsRemove?: (cartItemIds: string[]) => void | Promise<void>;
  onDesignGroupShellRemove?: () => void;
  designGroupKey: string;
  designGroupItems?: number[];
  designGroupName?: string;
}

interface StoreCartDesignExtendedProps extends StoreCartDesignProps {
  forceDesktopStyle?: boolean;
}

const StoreCartDesign: React.FC<StoreCartDesignExtendedProps> = ({
  cartDesign,
  formIndex,
  onCartItemRemove,
  onCartItemsRemove,
  onDesignGroupShellRemove,
  designGroupKey,
  designGroupItems,
  designGroupName = 'Design A',
  forceDesktopStyle = false,
}) => {
  const screen = useViewport(true);
  const isMobileCartLayout = !forceDesktopStyle && screen === ViewPort.mobile;
  // /store-cart page: fixed desktop class — never switches to mobile/tab viewport classes on resize.
  const styleClass = forceDesktopStyle
    ? 'store-cart-design--forced-desktop'
    : `store-cart-design--${screen}`;
  const dispatch = useAppDispatch();
  const { updateCartInfo, addMultipleToStore } = useCartActions();
  const redirectToWithUTM = useRedirectWithUTM();
  const { getValues, setValue, watch } = useFormContext();
  const [getQuote] = useGetQuoteMutation();
  const quoteRequestIdRef = useRef(0);
  const activeQuoteRequestIdRef = useRef(0);
  const isUserBreakpointChangeRef = useRef(false);
  const hasUserSelectedMinQuantityRef = useRef(false);
  const pinnedPriceListRef = useRef<IQuoteBreakpoints[] | null>(null);
  const hasSeededPriceListRef = useRef(false);
  const lastActiveQuoteRequestKeyRef = useRef('');
  const lastSelectedGroupQuoteRequestKeyRef = useRef('');
  const [isActiveQuoteLoading, setIsActiveQuoteLoading] = useState(false);
  const quoteAndPersistDesignGroupAtQuantityRef = useRef<
    (quantity: number, options?: { activeItemOnly?: boolean }) => Promise<void>
  >(() => Promise.resolve());

  // Get all items in this design group
  const watchedCartDetails = watch('cartDetails');
  const allCartItems = useMemo(() => watchedCartDetails ?? [], [watchedCartDetails]);
  const designGroupCartItems = useMemo(
    () =>
      designGroupItems?.length
        ? designGroupItems.map((index) => allCartItems[index]).filter(Boolean)
        : [cartDesign],
    [designGroupItems, allCartItems, cartDesign]
  );
  const resolvedDesignGroupCartItems = useMemo(
    () => (designGroupCartItems.length ? designGroupCartItems : [cartDesign]),
    [designGroupCartItems, cartDesign]
  );

  const designDetailsProofId = useMemo(
    () => getStoreCartDesignDetailsProofId(resolvedDesignGroupCartItems, cartDesign),
    [cartDesign, resolvedDesignGroupCartItems]
  );

  const { designItem, productOptions } = useStoreCartDesignGroupProductOptions({
    proofId: designDetailsProofId,
    cartItems: resolvedDesignGroupCartItems,
    fallbackProofItemId: cartDesign.proofItemId,
  });

  const [pinnedProductOptions, setPinnedProductOptions] = useState<IDesignGroupProductOption[]>(
    () => productOptions
  );
  const lastSyncedSelectionKeyRef = useRef('');
  const lastMoqTierSyncKeyRef = useRef('');
  const lastMergedProductOptionsKeyRef = useRef('');

  const productOptionsKey = useMemo(
    () => getDesignGroupProductOptionsKey(productOptions),
    [productOptions]
  );

  useEffect(() => {
    if (!productOptions.length || productOptionsKey === lastMergedProductOptionsKeyRef.current) {
      return;
    }

    setPinnedProductOptions((current) => {
      const merged = mergeDesignGroupProductOptions(current, productOptions);
      const mergedKey = getDesignGroupProductOptionsKey(merged);

      lastMergedProductOptionsKeyRef.current = mergedKey;

      if (mergedKey === getDesignGroupProductOptionsKey(current)) {
        return current;
      }

      return merged;
    });
  }, [productOptions, productOptionsKey]);

  const groupSelection = useAppSelector(
    (state) => state.storeCart.optionSelectionsByGroupKey[designGroupKey]
  );
  const groupSelectionRef = useRef(groupSelection);
  groupSelectionRef.current = groupSelection;
  const designGroupMoq = useAppSelector(
    (state) => state.storeCart.designGroupMoqByKey[designGroupKey]
  );
  const designGroupApiStatus = useAppSelector((state) =>
    getStoreCartDesignGroupApiStatus(state.storeCart.designGroupApiStatusByKey, designGroupKey)
  );

  const persistGroupSelection = useCallback(
    (selection: IStoreCartDesignGroupSelection) => {
      if (areStoreCartDesignGroupSelectionsEqual(groupSelectionRef.current, selection)) {
        return;
      }

      dispatch(
        setStoreCartDesignGroupSelection({
          groupKey: designGroupKey,
          selection,
        })
      );
    },
    [designGroupKey, dispatch]
  );

  const getDesignGroupFormIndices = useCallback(
    () => (designGroupItems?.length ? designGroupItems : [formIndex]),
    [designGroupItems, formIndex]
  );

  const syncFormLinesWithSelection = useCallback(
    (selection: IStoreCartDesignGroupSelection) => {
      const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
      const groupFormIndices = getDesignGroupFormIndices();

      groupFormIndices.forEach((idx) => {
        const item = cartDetails[idx];

        if (!item) {
          return;
        }

        const shouldBeActive = isCartItemIncludedInStoreOptionSelection(item, selection);

        if (item.isActive !== shouldBeActive) {
          setValue(`cartDetails.${idx}.isActive`, shouldBeActive, { shouldValidate: false });
          setValue(`cartDetails.${idx}.is_selected`, shouldBeActive ? 1 : 0, {
            shouldValidate: false,
          });
        }
      });
    },
    [getDesignGroupFormIndices, getValues, setValue]
  );

  const persistGroupSelectionAndSyncForm = useCallback(
    (selection: IStoreCartDesignGroupSelection) => {
      const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
      const groupFormIndices = getDesignGroupFormIndices();

      persistGroupSelection(selection);

      persistStoreCartDesignGroupSelection(
        groupFormIndices,
        cartDetails,
        selection,
        updateCartInfo
      ).catch(() => {
        showToastrMessage(STORE_CART_OPTION_INCLUSION_ERROR_MSG, 'failure');
      });

      syncFormLinesWithSelection(selection);
    },
    [
      getDesignGroupFormIndices,
      getValues,
      persistGroupSelection,
      syncFormLinesWithSelection,
      updateCartInfo,
    ]
  );

  const persistDesignGroupCartItems = useCallback(async () => {
    const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
    const groupFormIndices = getDesignGroupFormIndices();

    await persistStoreCartDesignGroupItemUpdates(groupFormIndices, cartDetails, updateCartInfo);
  }, [getDesignGroupFormIndices, getValues, updateCartInfo]);

  const addColorOptionToStoreCart = useCallback(
    async (colorOption: IDesignGroupColorOption, productOption: IDesignGroupProductOption) => {
      if (!designItem) {
        showToastrMessage(STORE_CART_ADD_COLOR_OPTION_ERROR_MSG, 'failure');
        return false;
      }

      const minQuantity = resolveStoreCartDesignGroupMinQuantity(resolvedDesignGroupCartItems);
      const payload = buildStoreCartAddPayloadForColorOption(
        designItem,
        colorOption,
        productOption,
        minQuantity
      );

      if (!payload) {
        showToastrMessage(STORE_CART_ADD_COLOR_OPTION_ERROR_MSG, 'failure');
        return false;
      }

      const outcome = await addMultipleToStore([payload]);

      if (outcome === 'failed') {
        showToastrMessage(STORE_CART_ADD_COLOR_OPTION_ERROR_MSG, 'failure');
        return false;
      }

      return true;
    },
    [addMultipleToStore, designItem, resolvedDesignGroupCartItems]
  );

  const resolvedDesignGroupCartItemsKey = useMemo(
    () => getStoreCartItemKeys(resolvedDesignGroupCartItems),
    [resolvedDesignGroupCartItems]
  );
  const groupSelectionKey = useMemo(
    () => serializeStoreCartDesignGroupSelection(groupSelection),
    [groupSelection]
  );

  useEffect(() => {
    if (!groupSelectionKey) {
      const initialSelection = buildStoreCartDesignGroupSelection(resolvedDesignGroupCartItems);

      if (isEmptyStoreCartDesignGroupSelection(initialSelection)) {
        return;
      }

      const initialSelectionKey = serializeStoreCartDesignGroupSelection(initialSelection);

      if (lastSyncedSelectionKeyRef.current === initialSelectionKey) {
        return;
      }

      lastSyncedSelectionKeyRef.current = initialSelectionKey;
      persistGroupSelection(initialSelection);
      syncFormLinesWithSelection(initialSelection);
      return;
    }

    if (lastSyncedSelectionKeyRef.current === groupSelectionKey) {
      return;
    }

    lastSyncedSelectionKeyRef.current = groupSelectionKey;
    syncFormLinesWithSelection(groupSelectionRef.current);
  }, [
    groupSelectionKey,
    persistGroupSelection,
    resolvedDesignGroupCartItems,
    resolvedDesignGroupCartItemsKey,
    syncFormLinesWithSelection,
  ]);

  const selectedColorKeysKey = groupSelection?.selectedColorKeys?.join('|') ?? '';
  const selectedProductStyleCodesKey = groupSelection?.selectedProductStyleCodes?.join('|') ?? '';
  const resolvedSelectedColorKeys = useMemo(
    () => new Set(groupSelection?.selectedColorKeys ?? []),
    [groupSelection?.selectedColorKeys]
  );
  const resolvedSelectedProductStyleCodes = useMemo(
    () => new Set(groupSelection?.selectedProductStyleCodes ?? []),
    [groupSelection?.selectedProductStyleCodes]
  );

  const visibleProductOptions = useMemo(() => {
    const baseOptions = pinnedProductOptions.length ? pinnedProductOptions : productOptions;
    const hasAnyColors = baseOptions.some((product) => product.colors.length > 0);

    if (hasAnyColors || !resolvedDesignGroupCartItems.length) {
      return baseOptions;
    }

    // Design-item responses can land with empty `colors`; backfill from cart lines.
    return mergeDesignGroupProductOptions(
      buildCartOnlyProductOptionsFromItems(resolvedDesignGroupCartItems),
      baseOptions
    );
  }, [pinnedProductOptions, productOptions, resolvedDesignGroupCartItems]);

  const visibleProductOptionsKey = useMemo(
    () => getDesignGroupProductOptionsKey(visibleProductOptions),
    [visibleProductOptions]
  );

  const [selectedProductStyleCode, setSelectedProductStyleCode] = useState<string>(
    () => visibleProductOptions[0]?.styleCode ?? ''
  );

  const activeProductOption = useMemo(() => {
    if (!visibleProductOptions.length) {
      return undefined;
    }

    return (
      visibleProductOptions.find((product) => product.styleCode === selectedProductStyleCode) ??
      visibleProductOptions[0]
    );
  }, [visibleProductOptions, selectedProductStyleCode]);

  useEffect(() => {
    if (!visibleProductOptions.length) {
      return;
    }

    const selectedProductStillExists = visibleProductOptions.some(
      (product) => product.styleCode === selectedProductStyleCode
    );
    const isCurrentProductSelected =
      !resolvedSelectedProductStyleCodes.size ||
      resolvedSelectedProductStyleCodes.has(
        normalizeStoreCartProductStyleCode(selectedProductStyleCode)
      );

    if (selectedProductStillExists && isCurrentProductSelected) {
      return;
    }

    const firstSelectedProduct = visibleProductOptions.find((product) =>
      resolvedSelectedProductStyleCodes.has(normalizeStoreCartProductStyleCode(product.styleCode))
    );

    setSelectedProductStyleCode(
      firstSelectedProduct?.styleCode ??
        (selectedProductStillExists ? selectedProductStyleCode : visibleProductOptions[0].styleCode)
    );
  }, [
    resolvedSelectedProductStyleCodes,
    selectedProductStyleCodesKey,
    selectedProductStyleCode,
    visibleProductOptions,
  ]);

  // Filter color options based on selected product option
  const filteredColorOptions = useMemo(() => {
    if (activeProductOption?.colors.length) {
      return activeProductOption.colors;
    }

    const styleCode = normalizeStoreCartProductStyleCode(
      activeProductOption?.styleCode || cartDesign.apparelStyleCode || ''
    );
    const cartOnlyOptions = buildCartOnlyProductOptionsFromItems(resolvedDesignGroupCartItems);

    if (!styleCode) {
      return cartOnlyOptions[0]?.colors ?? [];
    }

    return (
      cartOnlyOptions.find(
        (product) => normalizeStoreCartProductStyleCode(product.styleCode) === styleCode
      )?.colors ?? []
    );
  }, [activeProductOption, cartDesign.apparelStyleCode, resolvedDesignGroupCartItems]);

  const [selectedColorOption, setSelectedColorOption] = useState<IDesignGroupColorOption | null>(
    null
  );
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Prefer a cart-selected color so Price Per Item / quote target a real cart line.
  useEffect(() => {
    if (filteredColorOptions.length === 0) {
      return;
    }

    const styleCode = activeProductOption?.styleCode ?? '';
    const firstSelectedColor =
      filteredColorOptions.find((colorOption) =>
        resolvedSelectedColorKeys.has(
          buildStoreCartOptionSelectionKey(styleCode, colorOption.designId)
        )
      ) ?? filteredColorOptions[0];

    setSelectedColorOption((current) =>
      current?.designId === firstSelectedColor.designId ? current : firstSelectedColor
    );
  }, [
    activeProductOption?.styleCode,
    filteredColorOptions,
    resolvedSelectedColorKeys,
    selectedColorKeysKey,
  ]);

  // Find the current item based on selected product and color
  const currentItem = useMemo(() => {
    if (!activeProductOption || !selectedColorOption) {
      return resolvedDesignGroupCartItems[0] || cartDesign;
    }

    const normalizedProductStyleCode = normalizeStoreCartProductStyleCode(
      activeProductOption.styleCode
    );

    // Find the item that matches both the selected product and color
    const matchingItem = resolvedDesignGroupCartItems.find(
      (item) =>
        normalizeStoreCartProductStyleCode(item.apparelStyleCode ?? '') ===
          normalizedProductStyleCode && item.designId === selectedColorOption.designId
    );

    return matchingItem || resolvedDesignGroupCartItems[0] || cartDesign;
  }, [activeProductOption, selectedColorOption, resolvedDesignGroupCartItems, cartDesign]);

  const isCurrentItemBlockedForCheckout = isStoreCartItemBlockedForCheckout(currentItem);
  const designRejectionComment = currentItem.designRejectionInfo?.comment ?? '';
  const showDesignRejectionSection =
    isCurrentItemBlockedForCheckout && Boolean(designRejectionComment);

  const { data: similarProductsData } = useGetSimilarProductsQuery({
    styleCode: currentItem.apparelStyleCode ?? '',
    hexCode: currentItem.apparelHex ?? '',
    forceMode: true,
  });

  const isDesignToolDesign = Boolean(currentItem.fromDesignTool ?? cartDesign.fromDesignTool);
  const parentIsMto =
    colorNameIncludesMto(cartDesign.apparelColor) || checkIfMTO(cartDesign.decorMethodInfo ?? []);
  const addOptionDisabled = !isDesignToolDesign || parentIsMto;
  const addOptionTooltip = parentIsMto
    ? MTO_GROUP_ADD_DISABLED_TOOLTIP
    : DESIGN_TOOL_OPTIONS_DISABLED_TOOLTIP;

  const handleOpenDesignDetailsForColorOption = useCallback(() => {
    if (!isDesignToolDesign || parentIsMto) {
      return;
    }

    const proofItemId =
      selectedColorOption?.proofItemId || currentItem.proofItemId || cartDesign.proofItemId;

    if (!proofItemId) {
      showToastrMessage(STORE_CART_DESIGN_TOOL_COLOR_NAV_ERROR_MSG, 'failure');
      return;
    }

    redirectToWithUTM(
      buildDesignGroupDesignToolUrl({
        baseUrl: DESIGN_TOOL,
        proofItemId,
        fromDesignGroup: DESIGN_TOOL_FROM_DESIGN_GROUP_HQ_CART,
      }),
      '_blank'
    );
  }, [
    cartDesign.proofItemId,
    currentItem.proofItemId,
    isDesignToolDesign,
    parentIsMto,
    redirectToWithUTM,
    selectedColorOption?.proofItemId,
  ]);

  const handleOpenDesignDetailsForProductOption = useCallback(() => {
    if (!isDesignToolDesign || parentIsMto) {
      return;
    }

    const proofItemId =
      currentItem.proofItemId || selectedColorOption?.proofItemId || cartDesign.proofItemId;

    if (!proofItemId) {
      showToastrMessage(STORE_CART_DESIGN_TOOL_PRODUCT_NAV_ERROR_MSG, 'failure');
      return;
    }

    redirectToWithUTM(
      buildDesignGroupDesignToolUrl({
        baseUrl: DESIGN_TOOL,
        proofItemId,
        fromDesignGroup: DESIGN_TOOL_FROM_DESIGN_GROUP_HQ_CART,
      }),
      '_blank'
    );
  }, [
    cartDesign.proofItemId,
    currentItem.proofItemId,
    isDesignToolDesign,
    parentIsMto,
    redirectToWithUTM,
    selectedColorOption?.proofItemId,
  ]);

  // Find the form index for the current item
  const currentItemFormIndex = useMemo(() => {
    if (!designGroupItems) {
      return formIndex;
    }

    const normalizedCurrentStyleCode = normalizeStoreCartProductStyleCode(
      currentItem.apparelStyleCode ?? ''
    );
    const itemIndex = resolvedDesignGroupCartItems.findIndex(
      (item) =>
        normalizeStoreCartProductStyleCode(item.apparelStyleCode ?? '') ===
          normalizedCurrentStyleCode && item.designId === currentItem.designId
    );

    if (itemIndex >= 0 && designGroupItems[itemIndex] !== undefined) {
      return designGroupItems[itemIndex];
    }

    return formIndex;
  }, [currentItem, resolvedDesignGroupCartItems, designGroupItems, formIndex]);

  const checkOutOfStock = useCallback(() => {
    const formData = getValues();
    return Boolean(formData.cartDetails?.[currentItemFormIndex]?.outOfStock);
  }, [currentItemFormIndex, getValues]);

  const handleColorOptionPreview = useCallback((colorOption: IDesignGroupColorOption) => {
    setSelectedColorOption(colorOption);
  }, []);

  const handleProductOptionPreview = useCallback(
    (product: IDesignGroupProductOption) => {
      setSelectedProductStyleCode(product.styleCode);

      const firstSelectedColor = product.colors.find((colorOption) => {
        const selectionKey = buildStoreCartOptionSelectionKey(
          product.styleCode,
          colorOption.designId
        );

        return resolvedSelectedColorKeys.has(selectionKey);
      });

      setSelectedColorOption(firstSelectedColor ?? product.colors[0] ?? null);
    },
    [resolvedSelectedColorKeys]
  );

  const applyActiveOptionPreview = useCallback(
    (productStyleCode: string, color: IDesignGroupColorOption | null) => {
      setSelectedProductStyleCode(productStyleCode);
      setSelectedColorOption(color);
    },
    []
  );

  const handleColorOptionToggle = useCallback(
    (colorOption: IDesignGroupColorOption) => {
      if (!activeProductOption) {
        return;
      }

      const { styleCode } = activeProductOption;
      const normalizedStyleCode = normalizeStoreCartProductStyleCode(styleCode);
      const selectionKey = buildStoreCartOptionSelectionKey(styleCode, colorOption.designId);
      const isCurrentlySelected = resolvedSelectedColorKeys.has(selectionKey);

      if (!isCurrentlySelected) {
        setSelectedColorOption(colorOption);
      }

      if (isCurrentlySelected) {
        const selectedColorCount = countSelectedColorsForProduct(
          resolvedSelectedColorKeys,
          activeProductOption
        );

        if (selectedColorCount <= 1) {
          const nextProductKeys = new Set(resolvedSelectedProductStyleCodes);
          nextProductKeys.delete(normalizedStyleCode);

          if (
            wouldLeaveNoProductOptionsSelected(
              resolvedSelectedProductStyleCodes,
              normalizedStyleCode
            )
          ) {
            return;
          }

          const nextColorKeys = new Set(resolvedSelectedColorKeys);
          nextColorKeys.delete(selectionKey);
          const nextPreview = resolveNextProductPreviewAfterDeselect(
            visibleProductOptions,
            nextProductKeys,
            nextColorKeys,
            activeProductOption
          );

          if (nextPreview) {
            applyActiveOptionPreview(nextPreview.productStyleCode, nextPreview.colorOption);
          }

          persistGroupSelectionAndSyncForm({
            selectedColorKeys: Array.from(nextColorKeys),
            selectedProductStyleCodes: Array.from(nextProductKeys),
          });
          return;
        }

        const nextColorKeys = new Set(resolvedSelectedColorKeys);
        nextColorKeys.delete(selectionKey);

        if (
          wouldLeaveNoColorOptionsSelected(
            resolvedSelectedProductStyleCodes,
            nextColorKeys,
            visibleProductOptions
          )
        ) {
          return;
        }

        const nextColor = resolveNextColorPreviewAfterDeselect(
          activeProductOption,
          nextColorKeys,
          colorOption
        );

        if (nextColor) {
          setSelectedColorOption(nextColor);
        }

        persistGroupSelectionAndSyncForm({
          selectedColorKeys: Array.from(nextColorKeys),
          selectedProductStyleCodes: Array.from(resolvedSelectedProductStyleCodes),
        });
        return;
      }

      const nextColorKeys = new Set(resolvedSelectedColorKeys);
      nextColorKeys.add(selectionKey);
      const nextProductKeys = new Set(resolvedSelectedProductStyleCodes);
      nextProductKeys.add(normalizedStyleCode);
      persistGroupSelectionAndSyncForm({
        selectedColorKeys: Array.from(nextColorKeys),
        selectedProductStyleCodes: Array.from(nextProductKeys),
      });

      if (!isColorOptionInCart(resolvedDesignGroupCartItems, colorOption.designId, styleCode)) {
        addColorOptionToStoreCart(colorOption, activeProductOption).catch(() => undefined);
      }
    },
    [
      activeProductOption,
      addColorOptionToStoreCart,
      applyActiveOptionPreview,
      persistGroupSelectionAndSyncForm,
      resolvedDesignGroupCartItems,
      resolvedSelectedColorKeys,
      resolvedSelectedProductStyleCodes,
      visibleProductOptions,
    ]
  );

  const handleProductOptionToggle = useCallback(
    (product: IDesignGroupProductOption) => {
      const normalizedStyleCode = normalizeStoreCartProductStyleCode(product.styleCode);
      const isCurrentlySelected = resolvedSelectedProductStyleCodes.has(normalizedStyleCode);

      if (isCurrentlySelected) {
        if (
          wouldLeaveNoProductOptionsSelected(resolvedSelectedProductStyleCodes, normalizedStyleCode)
        ) {
          return;
        }

        const nextProductKeys = new Set(resolvedSelectedProductStyleCodes);
        nextProductKeys.delete(normalizedStyleCode);
        const nextColorKeys = clearProductColorSelectionKeys(resolvedSelectedColorKeys, product);

        if (
          wouldLeaveNoColorOptionsSelected(nextProductKeys, nextColorKeys, visibleProductOptions)
        ) {
          return;
        }

        const nextPreview = resolveNextProductPreviewAfterDeselect(
          visibleProductOptions,
          nextProductKeys,
          nextColorKeys,
          product
        );

        if (nextPreview) {
          applyActiveOptionPreview(nextPreview.productStyleCode, nextPreview.colorOption);
        }

        persistGroupSelectionAndSyncForm({
          selectedColorKeys: Array.from(nextColorKeys),
          selectedProductStyleCodes: Array.from(nextProductKeys),
        });
        return;
      }

      const nextProductKeys = new Set(resolvedSelectedProductStyleCodes);
      nextProductKeys.add(normalizedStyleCode);
      const nextColorKeys = ensureProductHasDefaultColorSelected(
        resolvedSelectedColorKeys,
        product
      );
      const nextColor = findFirstSelectedColorForProduct(product, nextColorKeys);

      applyActiveOptionPreview(product.styleCode, nextColor ?? product.colors[0] ?? null);

      persistGroupSelectionAndSyncForm({
        selectedColorKeys: Array.from(nextColorKeys),
        selectedProductStyleCodes: Array.from(nextProductKeys),
      });

      if (!isProductOptionInCart(resolvedDesignGroupCartItems, product.styleCode)) {
        const defaultColor = product.colors[0];

        if (defaultColor) {
          addColorOptionToStoreCart(defaultColor, product).catch(() => undefined);
        }
      }
    },
    [
      addColorOptionToStoreCart,
      applyActiveOptionPreview,
      persistGroupSelectionAndSyncForm,
      resolvedDesignGroupCartItems,
      resolvedSelectedColorKeys,
      resolvedSelectedProductStyleCodes,
      visibleProductOptions,
    ]
  );

  useEffect(() => {
    if (visibleProductOptions.length !== 1) {
      return;
    }

    const [singleProduct] = visibleProductOptions;
    const normalizedStyleCode = normalizeStoreCartProductStyleCode(singleProduct.styleCode);

    if (
      resolvedSelectedProductStyleCodes.has(normalizedStyleCode) &&
      countSelectedColorsForProduct(resolvedSelectedColorKeys, singleProduct) > 0
    ) {
      return;
    }

    const nextProductKeys = new Set(resolvedSelectedProductStyleCodes);
    nextProductKeys.add(normalizedStyleCode);
    const nextColorKeys = ensureProductHasDefaultColorSelected(
      resolvedSelectedColorKeys,
      singleProduct
    );
    const nextSelection = {
      selectedColorKeys: Array.from(nextColorKeys),
      selectedProductStyleCodes: Array.from(nextProductKeys),
    };

    if (areStoreCartDesignGroupSelectionsEqual(groupSelectionRef.current, nextSelection)) {
      return;
    }

    persistGroupSelectionAndSyncForm(nextSelection);
  }, [
    persistGroupSelectionAndSyncForm,
    resolvedSelectedColorKeys,
    resolvedSelectedProductStyleCodes,
    selectedColorKeysKey,
    selectedProductStyleCodesKey,
    visibleProductOptions,
    visibleProductOptionsKey,
  ]);

  const handleDesignGroupClose = useCallback(async () => {
    const cartItemIdsToRemove = [
      ...new Set(
        resolvedDesignGroupCartItems
          .map((item) => String(item.cartItemId))
          .filter((id) => id.length > 0 && id !== 'undefined')
      ),
    ];

    if (cartItemIdsToRemove.length > 0) {
      if (onCartItemsRemove) {
        await onCartItemsRemove(cartItemIdsToRemove);
      } else {
        await onCartItemRemove(formIndex);
      }
    }

    onDesignGroupShellRemove?.();
  }, [
    formIndex,
    onCartItemRemove,
    onCartItemsRemove,
    onDesignGroupShellRemove,
    resolvedDesignGroupCartItems,
  ]);

  const designGroupUsesFreshPrintsMinimums = useMemo(
    () =>
      resolvedDesignGroupCartItems.some((groupItem) =>
        isFreshPrintsApparelBrand(groupItem.apparelBrand)
      ),
    [resolvedDesignGroupCartItems]
  );

  const designGroupReferenceFormIndex = designGroupItems?.[0] ?? currentItemFormIndex;
  const watchedDesignGroupMinQuant = watch(`cartDetails.${designGroupReferenceFormIndex}.minQuant`);
  const watchedDesignGroupQuantity = watch(`cartDetails.${designGroupReferenceFormIndex}.quantity`);

  const designGroupReferenceMinQuant = useMemo(() => {
    const referenceItem = allCartItems[designGroupReferenceFormIndex];

    return referenceItem?.minQuant ?? referenceItem?.quantity;
  }, [allCartItems, designGroupReferenceFormIndex]);

  const resolvedDesignGroupMoqFloor = useMemo(
    () => resolveStoreCartDesignGroupMoqFloor(designGroupMoq, designGroupReferenceMinQuant),
    [designGroupMoq, designGroupReferenceMinQuant]
  );

  const quoteDesignGroupItemsAtQuantity = useCallback(
    async (
      quantity: number,
      requestId: number,
      options?: { activeItemOnly?: boolean }
    ): Promise<boolean> => {
      const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
      const groupFormIndices = designGroupItems?.length ? designGroupItems : [currentItemFormIndex];
      const selection = groupSelectionRef.current;

      const indicesToQuote = options?.activeItemOnly
        ? [currentItemFormIndex]
        : groupFormIndices.filter((groupItemIndex) => {
            const groupItem = cartDetails[groupItemIndex];

            return (
              Boolean(groupItem) && isCartItemIncludedInStoreOptionSelection(groupItem, selection)
            );
          });

      const results = await Promise.all(
        indicesToQuote.map(async (groupItemIndex) => {
          const groupItem = cartDetails[groupItemIndex];

          if (!groupItem || typeof groupItem.designId !== 'number') {
            return false;
          }

          if (options?.activeItemOnly) {
            const selectedDesignId = selectedColorOption?.designId;
            const selectedStyleCode = activeProductOption?.styleCode;

            if (selectedDesignId != null && groupItem.designId !== selectedDesignId) {
              return false;
            }

            if (
              selectedStyleCode &&
              normalizeStoreCartProductStyleCode(groupItem.apparelStyleCode ?? '') !==
                normalizeStoreCartProductStyleCode(selectedStyleCode)
            ) {
              return false;
            }
          }

          const groupTierBrand = designGroupUsesFreshPrintsMinimums
            ? 'Fresh Prints'
            : groupItem.apparelBrand;
          const existingPriceList = groupItem.priceList?.length
            ? groupItem.priceList
            : pinnedPriceListRef.current ?? cartDesign.priceList;

          const quote = await fetchStoreCartItemQuote(groupItem, cartDetails, quantity, getQuote);

          if (quoteRequestIdRef.current !== requestId || !quote) {
            return false;
          }

          applyStoreCartQuoteToFormItem(
            groupItemIndex,
            quantity,
            quote,
            groupTierBrand,
            setValue,
            existingPriceList,
            groupItem.price,
            groupItem.strikedPricePerUnit
          );

          return true;
        })
      );

      return results.some(Boolean);
    },
    [
      activeProductOption?.styleCode,
      cartDesign.priceList,
      currentItemFormIndex,
      designGroupItems,
      designGroupUsesFreshPrintsMinimums,
      getQuote,
      getValues,
      selectedColorOption?.designId,
      setValue,
    ]
  );

  const quoteAndPersistDesignGroupAtQuantity = useCallback(
    async (quantity: number, options?: { activeItemOnly?: boolean }): Promise<void> => {
      if (!Number.isFinite(quantity) || quantity <= 0) {
        return;
      }

      const requestId = quoteRequestIdRef.current + 1;
      quoteRequestIdRef.current = requestId;
      activeQuoteRequestIdRef.current = requestId;
      setIsActiveQuoteLoading(true);
      dispatch(setDesignGroupQuoteStatus({ groupKey: designGroupKey, status: 'loading' }));

      try {
        const didQuote = await quoteDesignGroupItemsAtQuantity(quantity, requestId, options);

        if (quoteRequestIdRef.current !== requestId) {
          return;
        }

        dispatch(
          setDesignGroupQuoteStatus({
            groupKey: designGroupKey,
            status: didQuote ? 'success' : 'error',
          })
        );

        if (didQuote) {
          await persistDesignGroupCartItems();
        } else if (!options?.activeItemOnly) {
          // Full-group quote failed — clear unit prices so we do not keep mock tiers.
          // Never wipe the whole group when an active-item-only quote misses/fails; that
          // was zeroing every sidebar line ($0.00) while the left pane still looked quoted.
          const groupFormIndices = designGroupItems?.length ? designGroupItems : [formIndex];
          const formOptions = { shouldValidate: false };

          groupFormIndices.forEach((groupItemIndex) => {
            setValue(`cartDetails.${groupItemIndex}.price`, 0, formOptions);
            setValue(`cartDetails.${groupItemIndex}.strikedPricePerUnit`, undefined, formOptions);
          });
          pinnedPriceListRef.current = null;
          hasSeededPriceListRef.current = false;
        }
      } catch {
        if (quoteRequestIdRef.current === requestId) {
          dispatch(setDesignGroupQuoteStatus({ groupKey: designGroupKey, status: 'error' }));
        }
      } finally {
        if (activeQuoteRequestIdRef.current === requestId) {
          setIsActiveQuoteLoading(false);
        }
      }
    },
    [
      designGroupItems,
      designGroupKey,
      dispatch,
      formIndex,
      persistDesignGroupCartItems,
      quoteDesignGroupItemsAtQuantity,
      setValue,
    ]
  );

  quoteAndPersistDesignGroupAtQuantityRef.current = quoteAndPersistDesignGroupAtQuantity;

  const designGroupTierBrand = designGroupUsesFreshPrintsMinimums
    ? 'Fresh Prints'
    : currentItem.apparelBrand;

  const designGroupMinQuantityTiers = useMemo(() => {
    if (!shouldShowStoreCartDesignGroupMoqTiers(designGroupApiStatus)) {
      return [];
    }

    const tierGroupFormIndices = designGroupItems?.length
      ? designGroupItems
      : [currentItemFormIndex];
    const referencePriceList = getStoreCartDesignGroupReferencePriceList(
      tierGroupFormIndices,
      allCartItems,
      cartDesign,
      designGroupTierBrand
    );

    if (hasUsableStoreCartPriceList(referencePriceList, designGroupTierBrand)) {
      const referenceTierCount = getDistinctBreakpointQuantitiesFromPriceList(
        referencePriceList,
        designGroupTierBrand
      ).length;
      const pinnedTierCount = getDistinctBreakpointQuantitiesFromPriceList(
        pinnedPriceListRef.current ?? undefined,
        designGroupTierBrand
      ).length;

      if (!pinnedPriceListRef.current || referenceTierCount >= pinnedTierCount) {
        pinnedPriceListRef.current = referencePriceList ?? null;
      }
    }

    const stablePriceList = pinnedPriceListRef.current ?? referencePriceList;

    return applyMoqFloorToMinQuantityTiers(
      getStoreCartDrawerMinQuantityTiers(stablePriceList),
      resolvedDesignGroupMoqFloor
    );
  }, [
    allCartItems,
    cartDesign,
    currentItemFormIndex,
    designGroupApiStatus,
    designGroupItems,
    designGroupTierBrand,
    resolvedDesignGroupMoqFloor,
  ]);

  const minQuantityOptions = useMemo(
    () =>
      designGroupMinQuantityTiers.map((tier) => ({
        label: String(tier),
        value: String(tier),
      })),
    [designGroupMinQuantityTiers]
  );

  const defaultDesignGroupMinQuantity = designGroupMinQuantityTiers[0];

  const designGroupBreakpointQuantity = useMemo(() => {
    const resolvedQuantity = Number(watchedDesignGroupMinQuant ?? watchedDesignGroupQuantity);

    if (Number.isFinite(resolvedQuantity) && resolvedQuantity > 0) {
      return resolvedQuantity;
    }

    return defaultDesignGroupMinQuantity;
  }, [defaultDesignGroupMinQuantity, watchedDesignGroupMinQuant, watchedDesignGroupQuantity]);

  const fetchSelectedDesignGroupQuote = useCallback(
    async (quantity: number) => {
      await quoteAndPersistDesignGroupAtQuantity(quantity);
    },
    [quoteAndPersistDesignGroupAtQuantity]
  );

  // Quote every selected cart line for the shared design-group minimum so the sidebar
  // shows live quoter prices instead of a stale static unit price.
  useEffect(() => {
    if (isUserBreakpointChangeRef.current || isActiveQuoteLoading) {
      return;
    }

    if (!Number.isFinite(designGroupBreakpointQuantity) || designGroupBreakpointQuantity <= 0) {
      return;
    }

    const requestKey = `selected-group:${designGroupKey}:${designGroupBreakpointQuantity}:${selectedColorKeysKey}:${selectedProductStyleCodesKey}`;

    if (lastSelectedGroupQuoteRequestKeyRef.current === requestKey) {
      return;
    }

    const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
    const groupFormIndices = designGroupItems?.length ? designGroupItems : [currentItemFormIndex];
    const hasQuotableSelectedItem = groupFormIndices.some((groupItemIndex) => {
      const groupItem = cartDetails[groupItemIndex];

      return (
        Boolean(groupItem?.cartItemId) &&
        typeof groupItem?.designId === 'number' &&
        isCartItemIncludedInStoreOptionSelection(groupItem, groupSelection)
      );
    });

    if (!hasQuotableSelectedItem) {
      return;
    }

    lastSelectedGroupQuoteRequestKeyRef.current = requestKey;

    const selectedDesignId = selectedColorOption?.designId;
    const selectedStyleCode = activeProductOption?.styleCode;

    if (selectedDesignId != null && selectedStyleCode) {
      lastActiveQuoteRequestKeyRef.current = `${normalizeStoreCartProductStyleCode(
        selectedStyleCode
      )}:${selectedDesignId}:${designGroupBreakpointQuantity}:${currentItemFormIndex}`;
    }

    fetchSelectedDesignGroupQuote(designGroupBreakpointQuantity).catch(() => {
      lastSelectedGroupQuoteRequestKeyRef.current = '';
      dispatch(setDesignGroupQuoteStatus({ groupKey: designGroupKey, status: 'error' }));
    });
  }, [
    activeProductOption?.styleCode,
    currentItemFormIndex,
    designGroupBreakpointQuantity,
    designGroupItems,
    designGroupKey,
    dispatch,
    fetchSelectedDesignGroupQuote,
    getValues,
    groupSelection,
    isActiveQuoteLoading,
    selectedColorKeysKey,
    selectedColorOption?.designId,
    selectedProductStyleCodesKey,
  ]);

  useEffect(() => {
    if (!shouldShowStoreCartDesignGroupPrice(designGroupApiStatus)) {
      return;
    }

    if (hasSeededPriceListRef.current) {
      return;
    }

    const seedGroupFormIndices = designGroupItems?.length
      ? designGroupItems
      : [currentItemFormIndex];
    const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
    const seedPriceList =
      pinnedPriceListRef.current ??
      (hasUsableStoreCartPriceList(cartDesign.priceList, designGroupTierBrand)
        ? cartDesign.priceList
        : undefined);

    if (!seedPriceList?.length) {
      return;
    }

    let didSeed = false;

    seedGroupFormIndices.forEach((groupItemIndex) => {
      const groupItem = cartDetails[groupItemIndex];

      if (!groupItem) {
        return;
      }

      const groupTierBrand = designGroupUsesFreshPrintsMinimums
        ? 'Fresh Prints'
        : groupItem.apparelBrand;

      if (hasUsableStoreCartPriceList(groupItem.priceList, groupTierBrand)) {
        return;
      }

      setValue(`cartDetails.${groupItemIndex}.priceList`, seedPriceList, { shouldValidate: false });
      didSeed = true;
    });

    if (didSeed) {
      hasSeededPriceListRef.current = true;
    }
  }, [
    cartDesign.priceList,
    currentItemFormIndex,
    designGroupApiStatus,
    designGroupItems,
    designGroupTierBrand,
    designGroupUsesFreshPrintsMinimums,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!shouldShowStoreCartDesignGroupMoqTiers(designGroupApiStatus)) {
      return;
    }

    // Once the user explicitly picks a minimum tier, never auto-reset it back to the
    // hydration floor (e.g. selecting 12 must not jump to 1).
    if (hasUserSelectedMinQuantityRef.current) {
      return;
    }

    if (!designGroupItems?.length) {
      return;
    }

    if (!designGroupMinQuantityTiers.length || defaultDesignGroupMinQuantity == null) {
      return;
    }

    const referenceIndex = designGroupItems[0];
    const cartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
    const referenceItem = cartDetails[referenceIndex];

    if (!referenceItem) {
      return;
    }

    const currentMin = Number(referenceItem.minQuant ?? referenceItem.quantity ?? 0);

    // Keep a valid persisted tier unless this is still an unset initial add (no
    // saved minimum) while MOQ hydrated to a lower first breakpoint.
    if (
      designGroupMinQuantityTiers.includes(currentMin) &&
      !shouldResetStoreCartDesignGroupToDefaultMinimum(
        currentMin,
        defaultDesignGroupMinQuantity,
        referenceItem.savedMinimumQuantity
      )
    ) {
      lastMoqTierSyncKeyRef.current = `${referenceIndex}:${currentMin}`;
      return;
    }

    if (designGroupMoq == null) {
      const drawerDefault = STORE_CART_DRAWER_MIN_QUANTITY_TIERS[0];

      if (currentMin > 0 && currentMin < drawerDefault) {
        return;
      }
    }

    const nextMin = defaultDesignGroupMinQuantity;
    const syncKey = `${referenceIndex}:${nextMin}`;

    if (lastMoqTierSyncKeyRef.current === syncKey) {
      return;
    }

    lastMoqTierSyncKeyRef.current = syncKey;

    const tierGroupFormIndices = designGroupItems.length
      ? designGroupItems
      : [currentItemFormIndex];

    applyStoreCartDrawerDesignGroupTierPrices(
      nextMin,
      tierGroupFormIndices,
      cartDetails,
      cartDesign,
      setValue
    );

    // Mark the hydrated floor as an explicit saved minimum so cart remaps keep it
    // (e.g. MOQ floor 16 outside the static [12, 24, ...] drawer list).
    tierGroupFormIndices.forEach((groupItemIndex) => {
      setValue(`cartDetails.${groupItemIndex}.savedMinimumQuantity`, nextMin, {
        shouldValidate: false,
      });
    });

    const updatedCartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
    persistStoreCartDesignGroupMinimums(updatedCartDetails);
  }, [
    cartDesign,
    currentItemFormIndex,
    defaultDesignGroupMinQuantity,
    designGroupApiStatus,
    designGroupItems,
    designGroupMinQuantityTiers,
    designGroupMoq,
    getValues,
    setValue,
  ]);

  // Render the unified item card
  const renderItem = () => {
    const item = currentItem;
    const itemFormIndex = currentItemFormIndex;
    // Use getValues to get the latest values immediately
    const cartDetails = getValues('cartDetails') || [];
    const formItem = cartDetails[itemFormIndex] || item;
    const itemQuantityWatch = watch(`cartDetails.${itemFormIndex}.quantity`) ?? formItem?.quantity;
    const itemMinQuantWatch = watch(`cartDetails.${itemFormIndex}.minQuant`) ?? formItem?.minQuant;
    const itemPriceListWatch = watch(`cartDetails.${itemFormIndex}.priceList`);
    const itemPriceWatch = watch(`cartDetails.${itemFormIndex}.price`);
    // Get print types and decor methods for this item
    // Current values for this item
    const itemCurrentMinQuantity = String(
      itemMinQuantWatch ?? itemQuantityWatch ?? item.minQuant ?? defaultDesignGroupMinQuantity
    );
    const resolveItemPriceList = (): IQuoteBreakpoints[] => {
      if (hasUsableStoreCartPriceList(itemPriceListWatch, designGroupTierBrand)) {
        return itemPriceListWatch;
      }

      if (formItem?.priceList?.length) {
        return formItem.priceList;
      }

      if (pinnedPriceListRef.current?.length) {
        return pinnedPriceListRef.current;
      }

      return cartDesign.priceList ?? [];
    };
    const priceList = resolveItemPriceList();
    const resolveItemCurrentPrice = (): number | null => {
      const selectedQty = Number(itemCurrentMinQuantity);
      const formPrice =
        typeof itemPriceWatch === 'number' ? itemPriceWatch : Number(formItem?.price) || 0;
      const formMin = Number(
        typeof itemMinQuantWatch === 'number'
          ? itemMinQuantWatch
          : formItem?.minQuant ?? itemMinQuantWatch ?? 0
      );
      const exactListPrice = getStoreCartPriceFromPriceListOnly(selectedQty, priceList, {
        exactQuantity: true,
      });

      // Prefer an exact quoted breakpoint for the selected minimum.
      if (exactListPrice != null && exactListPrice > 0) {
        return exactListPrice;
      }

      // After applyStoreCartDrawer clears price, formPrice is 0 until the quoter
      // responds — show shimmer instead of a stale lower-tier unit price.
      if (formPrice > 0 && formMin === selectedQty && !priceList?.length) {
        return formPrice;
      }

      if (!(formPrice > 0)) {
        if (isCurrentItemBlockedForCheckout) {
          const fallbackPrice =
            Number(item.apparelPrice) || Number(item.baseUnitPrice) || Number(item.price) || 0;
          return fallbackPrice > 0 ? fallbackPrice : null;
        }

        return null;
      }

      if (isCurrentItemBlockedForCheckout) {
        const fallbackPrice =
          Number(formItem?.price) ||
          Number(item.apparelPrice) ||
          Number(item.baseUnitPrice) ||
          Number(item.price) ||
          0;
        return fallbackPrice > 0 ? fallbackPrice : null;
      }

      if (!shouldShowStoreCartDesignGroupPrice(designGroupApiStatus)) {
        return null;
      }

      return null;
    };
    const itemCurrentPrice = resolveItemCurrentPrice();

    const renderPricePerItemValue = () => {
      if (
        !isCurrentItemBlockedForCheckout &&
        (hasStoreCartDesignGroupQuoteFailed(designGroupApiStatus) ||
          isActiveQuoteLoading ||
          isStoreCartDesignGroupQuotePending(designGroupApiStatus) ||
          itemCurrentPrice == null)
      ) {
        return (
          <div
            className={`${styleClass}__minimum-section__price-row`}
            aria-busy="true"
            aria-label={STORE_CART_PRICE_LOADING_ARIA_LABEL}
          >
            <div className={`${styleClass}__minimum-section__price-shimmer`} aria-hidden="true" />
          </div>
        );
      }

      if (itemCurrentPrice == null) {
        return (
          <div
            className={`${styleClass}__minimum-section__price-row`}
            aria-busy="true"
            aria-label={STORE_CART_PRICE_LOADING_ARIA_LABEL}
          >
            <div className={`${styleClass}__minimum-section__price-shimmer`} aria-hidden="true" />
          </div>
        );
      }

      return (
        <div className={`${styleClass}__minimum-section__price-row`}>
          <Text as="span" className={`${styleClass}__minimum-section__price`}>
            {currencyFormat(itemCurrentPrice)}
          </Text>
        </div>
      );
    };

    const handleItemMinQuantityChange = (value: string | number | string[] | number[]) => {
      const quantityValue = Array.isArray(value) ? value[0] : value;
      const quantityNumber =
        typeof quantityValue === 'string' ? Number(quantityValue) : quantityValue;

      if (!Number.isNaN(quantityNumber) && quantityNumber > 0) {
        isUserBreakpointChangeRef.current = true;
        hasUserSelectedMinQuantityRef.current = true;

        const selectedDesignId = selectedColorOption?.designId;
        const selectedStyleCode = activeProductOption?.styleCode;
        if (selectedDesignId != null && selectedStyleCode) {
          lastActiveQuoteRequestKeyRef.current = `${normalizeStoreCartProductStyleCode(
            selectedStyleCode
          )}:${selectedDesignId}:${quantityNumber}:${currentItemFormIndex}`;
        }

        lastSelectedGroupQuoteRequestKeyRef.current = `selected-group:${designGroupKey}:${quantityNumber}:${selectedColorKeysKey}:${selectedProductStyleCodesKey}`;

        const latestCartDetails: ICartItemDetail[] = getValues('cartDetails') || [];
        const minQuantityGroupFormIndices = designGroupItems?.length
          ? designGroupItems
          : [itemFormIndex];

        applyStoreCartDrawerDesignGroupTierPrices(
          quantityNumber,
          minQuantityGroupFormIndices,
          latestCartDetails,
          cartDesign,
          setValue
        );

        // Keep an explicit user choice so later cart remaps don't lose the selection.
        minQuantityGroupFormIndices.forEach((groupItemIndex) => {
          setValue(`cartDetails.${groupItemIndex}.savedMinimumQuantity`, quantityNumber, {
            shouldValidate: false,
          });
        });

        const cartDetailsAfterMinChange: ICartItemDetail[] = getValues('cartDetails') || [];
        persistStoreCartDesignGroupMinimums(cartDetailsAfterMinChange);
        // Persist quantity immediately (before quote) so closing the cart mid-quote
        // still restores the selected minimum on reopen.
        persistDesignGroupCartItems().catch(() => undefined);

        quoteAndPersistDesignGroupAtQuantityRef
          .current(quantityNumber)
          .catch(() => undefined)
          .finally(() => {
            isUserBreakpointChangeRef.current = false;
          });
      }
    };

    const renderMinQuantityOptions = () => {
      if (isStoreCartDesignGroupMoqPending(designGroupApiStatus)) {
        return (
          <div
            className={`${styleClass}__minimum-section__moq-shimmer`}
            aria-busy="true"
            aria-label={STORE_CART_MOQ_LOADING_ARIA_LABEL}
          />
        );
      }

      // Success, failure, or maxMoq 0 all render tiers (defaults when MOQ is unavailable)
      return minQuantityOptions.map((option) => {
        const isSelected = itemCurrentMinQuantity === option.value;

        return (
          <button
            key={option.value}
            type="button"
            className={`${styleClass}__minimum-section__button ${
              isSelected ? `${styleClass}__minimum-section__button--selected` : ''
            }`}
            onClick={() => handleItemMinQuantityChange(option.value)}
          >
            {option.label}
          </button>
        );
      });
    };

    // Get color options for this specific item (simplified - using all design group colors)
    const itemColorOptions = filteredColorOptions;

    return (
      <>
        {/* Main Content */}
        <div className={`${styleClass}__content`}>
          {/* Main Product Image - Use selected color option's image if available */}
          <HoverableImage
            item={
              selectedColorOption
                ? {
                    ...item,
                    primaryImage: selectedColorOption.primaryImage || item.primaryImage,
                    frontImage: selectedColorOption.primaryImage || item.frontImage,
                    backImage: selectedColorOption.secondaryImage || item.backImage,
                    designImgPrimary: selectedColorOption.designImage || item.designImgPrimary,
                  }
                : item
            }
            styleClass={styleClass}
            forceDesktopStyle={forceDesktopStyle}
          />

          {/* Right: Product Details */}
          <div className={`${styleClass}__details`}>
            {/* Header with Design Group and Close Button */}
            <div className={`${styleClass}__header-section`}>
              <div className={`${styleClass}__header-section__top-row`}>
                <div className={`${styleClass}__header-section__design-group-row`}>
                  <Text
                    as="div"
                    className={`${styleClass}__header-section__design-group`}
                    size={13}
                    color="#8D90AA"
                  >
                    {designGroupName}
                  </Text>
                  <ToolTip
                    title={STORE_CART_DESIGN_GROUP_HELP_TOOLTIP}
                    placement="top"
                    getPopupContainer={getBodyTooltipContainer}
                    zIndex={STORE_MODAL_TOOLTIP_Z_INDEX}
                  >
                    <button
                      type="button"
                      className={`${styleClass}__header-section__design-group-help`}
                      aria-label={STORE_CART_DESIGN_GROUP_HELP_TOOLTIP}
                    >
                      ?
                    </button>
                  </ToolTip>
                </div>
              </div>
              <button
                type="button"
                className={`${styleClass}__close`}
                aria-label="Remove item from store cart"
                onClick={() => {
                  handleDesignGroupClose().catch(() => undefined);
                }}
              >
                <Icon source="/src/assets/svg/remove.svg" width="20px" height="20px" />
              </button>
              <Text as="h2" className={`${styleClass}__product-name`} weight="semibold">
                {activeProductOption?.apparelName ||
                  item.apparelName ||
                  'Comfort Colors Adult Heavyweight T-Shirt'}
              </Text>
            </div>

            {/* Product Options and Color Options */}
            <div className={`${styleClass}__options-row`}>
              {/* Product Options Section */}
              {visibleProductOptions.length > 0 && (
                <div className={`${styleClass}__options-row__section`}>
                  <Text as="label" className={`${styleClass}__options-row__label`}>
                    Product Options
                  </Text>
                  <div className={`${styleClass}__options-row__items`}>
                    {visibleProductOptions.map((product) => {
                      const isProductSelected = resolvedSelectedProductStyleCodes.has(
                        normalizeStoreCartProductStyleCode(product.styleCode)
                      );
                      const isActiveProduct = activeProductOption?.styleCode === product.styleCode;
                      let productItemStateClass = `${styleClass}__options-row__product-item--selected`;

                      if (isActiveProduct) {
                        productItemStateClass = `${styleClass}__options-row__product-item--active`;
                      } else if (!isProductSelected) {
                        productItemStateClass = `${styleClass}__options-row__product-item--inactive`;
                      }

                      return (
                        <div
                          key={product.styleCode}
                          className={`${styleClass}__options-row__product-item-wrapper${
                            isProductSelected || isActiveProduct
                              ? ''
                              : ` ${styleClass}__options-row__product-item-wrapper--unselected`
                          }`}
                        >
                          <button
                            type="button"
                            className={`${styleClass}__options-row__product-item ${productItemStateClass}`}
                            aria-label={`View product ${product.apparelName}`}
                            onClick={() => handleProductOptionPreview(product)}
                          >
                            <img
                              src={
                                isActiveProduct && selectedColorOption
                                  ? selectedColorOption.primaryImage
                                  : product.primaryImage
                              }
                              alt={product.apparelName}
                              onError={(e) => {
                                e.currentTarget.src = '/src/assets/svg/image_error.svg';
                              }}
                            />
                          </button>
                          <button
                            type="button"
                            className={`${styleClass}__options-row__option-control${
                              isProductSelected
                                ? ` ${styleClass}__options-row__option-control--selected`
                                : ` ${styleClass}__options-row__option-control--add`
                            }`}
                            aria-label={
                              isProductSelected
                                ? STORE_CART_DESELECT_PRODUCT_OPTION_ARIA_LABEL
                                : STORE_CART_SELECT_PRODUCT_OPTION_ARIA_LABEL
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              handleProductOptionToggle(product);
                            }}
                          >
                            <Icon
                              source={
                                isProductSelected
                                  ? STORE_CART_OPTION_CANCEL_ICON_SRC
                                  : STORE_CART_OPTION_RESELECT_ICON_SRC
                              }
                              className={`${styleClass}__options-row__option-control__icon`}
                            />
                          </button>
                        </div>
                      );
                    })}
                    {addOptionDisabled ? (
                      <ToolTip
                        title={addOptionTooltip}
                        getPopupContainer={getBodyTooltipContainer}
                        zIndex={STORE_MODAL_TOOLTIP_Z_INDEX}
                      >
                        <button
                          type="button"
                          className={`${styleClass}__options-row__add-product`}
                          aria-label={STORE_CART_ADD_PRODUCT_OPTION_ARIA_LABEL}
                          disabled
                        >
                          <Icon
                            source="/src/assets/svg/plus_blue.svg"
                            width="14px"
                            height="14px"
                            className={`${styleClass}__options-row__add-product__icon`}
                          />
                        </button>
                      </ToolTip>
                    ) : (
                      <button
                        type="button"
                        className={`${styleClass}__options-row__add-product`}
                        aria-label={STORE_CART_ADD_PRODUCT_OPTION_ARIA_LABEL}
                        onClick={handleOpenDesignDetailsForProductOption}
                      >
                        <Icon
                          source="/src/assets/svg/plus_blue.svg"
                          width="14px"
                          height="14px"
                          className={`${styleClass}__options-row__add-product__icon`}
                        />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {visibleProductOptions.length > 0 && (
                <div className={`${styleClass}__options-row__divider`} />
              )}
              {/* Color Options Section */}
              <div className={`${styleClass}__options-row__section`}>
                <Text as="label" className={`${styleClass}__options-row__label`}>
                  Color Options
                </Text>
                <div className={`${styleClass}__options-row__color-list`}>
                  {itemColorOptions.map((color) => {
                    const selectionKey =
                      activeProductOption &&
                      buildStoreCartOptionSelectionKey(
                        activeProductOption.styleCode,
                        color.designId
                      );
                    const isColorSelected = Boolean(
                      selectionKey && resolvedSelectedColorKeys.has(selectionKey)
                    );
                    const isActiveColor = selectedColorOption?.designId === color.designId;
                    let colorItemStateClass = `${styleClass}__options-row__color-item--selected`;

                    if (isActiveColor) {
                      colorItemStateClass = `${styleClass}__options-row__color-item--active`;
                    } else if (!isColorSelected) {
                      colorItemStateClass = `${styleClass}__options-row__color-item--inactive`;
                    }

                    return (
                      <div
                        key={color.designId}
                        className={`${styleClass}__options-row__color-item-wrapper${
                          isColorSelected || isActiveColor
                            ? ''
                            : ` ${styleClass}__options-row__color-item-wrapper--unselected`
                        }`}
                      >
                        <button
                          type="button"
                          className={`${styleClass}__options-row__color-item ${colorItemStateClass}${
                            isLightStoreCartColorHex(color.colorHex)
                              ? ` ${styleClass}__options-row__color-item--light`
                              : ''
                          }`}
                          aria-label={`View color ${color.colorName}`}
                          onClick={() => handleColorOptionPreview(color)}
                        >
                          <ColorPill
                            backgroundColor={color.colorHex}
                            colorName={color.colorName}
                            size="medium"
                            showTooltip={false}
                            type="div"
                          />
                        </button>
                        <button
                          type="button"
                          className={`${styleClass}__options-row__option-control${
                            isColorSelected
                              ? ` ${styleClass}__options-row__option-control--selected`
                              : ` ${styleClass}__options-row__option-control--add`
                          }`}
                          aria-label={
                            isColorSelected
                              ? STORE_CART_DESELECT_COLOR_OPTION_ARIA_LABEL
                              : STORE_CART_SELECT_COLOR_OPTION_ARIA_LABEL
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            handleColorOptionToggle(color);
                          }}
                        >
                          <Icon
                            source={
                              isColorSelected
                                ? STORE_CART_OPTION_CANCEL_ICON_SRC
                                : STORE_CART_OPTION_RESELECT_ICON_SRC
                            }
                            className={`${styleClass}__options-row__option-control__icon`}
                          />
                        </button>
                      </div>
                    );
                  })}
                  {addOptionDisabled ? (
                    <ToolTip
                      title={addOptionTooltip}
                      getPopupContainer={getBodyTooltipContainer}
                      zIndex={STORE_MODAL_TOOLTIP_Z_INDEX}
                    >
                      <button
                        type="button"
                        className={`${styleClass}__options-row__add-color`}
                        aria-label={STORE_CART_ADD_COLOR_OPTION_ARIA_LABEL}
                        disabled
                      >
                        <Icon
                          source="/src/assets/svg/plus_blue.svg"
                          width="14px"
                          height="14px"
                          className={`${styleClass}__options-row__add-color__icon`}
                        />
                      </button>
                    </ToolTip>
                  ) : (
                    <button
                      type="button"
                      className={`${styleClass}__options-row__add-color`}
                      aria-label={STORE_CART_ADD_COLOR_OPTION_ARIA_LABEL}
                      onClick={handleOpenDesignDetailsForColorOption}
                    >
                      <Icon
                        source="/src/assets/svg/plus_blue.svg"
                        width="14px"
                        height="14px"
                        className={`${styleClass}__options-row__add-color__icon`}
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Select Minimum Section */}
            <div className={`${styleClass}__minimum-section`}>
              <div className={`${styleClass}__minimum-section__wrapper`}>
                {isMobileCartLayout ? (
                  <>
                    <div className={`${styleClass}__minimum-section__input-wrapper`}>
                      <div className={`${styleClass}__minimum-section__left-wrapper`}>
                        <Text as="label" className={`${styleClass}__minimum-section__label`}>
                          Select Minimum
                        </Text>
                        <div className={`${styleClass}__minimum-section__buttons-wrapper`}>
                          <div className={`${styleClass}__minimum-section__buttons`}>
                            {renderMinQuantityOptions()}
                          </div>
                        </div>
                      </div>
                      <div className={`${styleClass}__minimum-section__divider`} />
                      <div className={`${styleClass}__minimum-section__price-wrapper`}>
                        <Text as="label" className={`${styleClass}__minimum-section__label`}>
                          Price Per Item
                        </Text>
                        {renderPricePerItemValue()}
                      </div>
                    </div>
                    {shouldShowStoreCartDesignGroupMoqTiers(designGroupApiStatus) && (
                      <div className={`${styleClass}__minimum-info-box`}>
                        <Text
                          as="div"
                          className={`${styleClass}__minimum-info-box__text`}
                          size={13}
                        >
                          {getStoreDesignMinimumSubtitle(Number(itemCurrentMinQuantity))}
                        </Text>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Text as="label" className={`${styleClass}__minimum-section__label`}>
                      Select Minimum
                    </Text>
                    <div className={`${styleClass}__minimum-section__input-wrapper`}>
                      <div className={`${styleClass}__minimum-section__left-wrapper`}>
                        <div className={`${styleClass}__minimum-section__buttons-wrapper`}>
                          <div className={`${styleClass}__minimum-section__buttons`}>
                            {renderMinQuantityOptions()}
                          </div>
                          {shouldShowStoreCartDesignGroupMoqTiers(designGroupApiStatus) && (
                            <div className={`${styleClass}__minimum-info-box`}>
                              <Text
                                as="div"
                                className={`${styleClass}__minimum-info-box__text`}
                                size={13}
                              >
                                {getStoreDesignMinimumSubtitle(Number(itemCurrentMinQuantity))}
                              </Text>
                            </div>
                          )}
                        </div>
                        <div className={`${styleClass}__minimum-section__divider`} />
                      </div>
                      <div className={`${styleClass}__minimum-section__price-wrapper`}>
                        <Text as="label" className={`${styleClass}__minimum-section__label`}>
                          Price Per Item
                        </Text>
                        {renderPricePerItemValue()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styleClass}>
      {/* Single unified card */}
      <div ref={cardRef} className={`${styleClass}__card`}>
        {showDesignRejectionSection && (
          <div className="stock-info-container">
            <div className={`${styleClass}__design-rejection-section`}>
              {designRejectionComment}
            </div>
          </div>
        )}
        <div
          className={
            isCurrentItemBlockedForCheckout ? `${styleClass}__card-content--blocked` : undefined
          }
          style={checkOutOfStock() ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          {!showDesignRejectionSection && (
            <div className="stock-info-container">
              <StockInfoCard
                proofItemId={currentItem.proofItemId}
                styleCode={currentItem.apparelStyleCode}
                colorCode={currentItem.apparelHex ?? ''}
                apparelColorCode={currentItem.apparelColorCode ?? ''}
                formIndex={currentItemFormIndex}
                designTool={Boolean(currentItem.fromDesignTool)}
                getValues={getValues}
                setValue={setValue}
                color={currentItem.apparelColor ?? ''}
                source="HQ"
                productBrand={currentItem.apparelBrand ?? ''}
                section="HQ Cart"
                seeMoreUrl={similarProductsData?.seeMoreURL ?? ''}
                isStoreCart
                hideSwitchOptions
              />
            </div>
          )}
          {renderItem()}
        </div>
      </div>
    </div>
  );
};

StoreCartDesign.displayName = 'StoreCartDesign';

// Don't memoize to ensure re-renders when form values change
export default StoreCartDesign;
